import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { SEOHead } from '../../components/seo/SEOHead';
import { Button } from '../../components/ui/Button';
import { Spinner } from '../../components/ui/Card';
import { api } from '../../lib/api';
import { launchRazorpayCheckout } from '../../lib/razorpay';
import type { ConsultationBooking } from '@bharat-consultancy/shared';

export default function ConsultSummary() {
  const [searchParams] = useSearchParams();
  const bookingId = searchParams.get('bookingId');
  const navigate = useNavigate();

  const [booking, setBooking] = useState<ConsultationBooking | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isPaying, setIsPaying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!bookingId) return;
    api.consultations
      .get(bookingId)
      .then((res) => setBooking(res.booking))
      .catch(() => setError('Could not load your booking details.'))
      .finally(() => setIsLoading(false));
  }, [bookingId]);

  async function handleProceedToPay() {
    if (!booking) return;
    setIsPaying(true);
    setError(null);
    try {
      const checkoutParams = await api.consultations.pay(booking.id);
      await launchRazorpayCheckout({
        keyId: checkoutParams.razorpayKeyId,
        amount: checkoutParams.amount,
        currency: checkoutParams.currency,
        razorpayOrderId: checkoutParams.razorpayOrderId,
        customerName: booking.name,
        customerEmail: booking.email,
        customerPhone: booking.mobile,
        onSuccess: () => {
          // As with the main order flow, fulfillment (marking the
          // booking "scheduled" + sending confirmation emails) happens
          // via the server-verified Razorpay webhook, not this client
          // callback — see paymentService.ts. This redirect is just UX.
          navigate(`/consult/thank-you?bookingId=${booking.id}`);
        },
        onDismiss: () => setIsPaying(false),
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not start payment. Please try again.');
      setIsPaying(false);
    }
  }

  if (isLoading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Spinner className="h-8 w-8 text-seal-blue" />
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="mx-auto max-w-xl px-4 py-20 text-center">
        <h1 className="font-display text-2xl font-bold text-ink">Booking Not Found</h1>
      </div>
    );
  }

  return (
    <>
      <SEOHead title="Consultation Booking Summary — Bharat Consultancy" description="" canonicalPath="/consult/summary" />

      <section className="bg-paper py-10">
        <div className="mx-auto max-w-2xl px-4 sm:px-6">
          <div className="rounded-lg border border-line bg-white p-6">
            <h1 className="font-display text-2xl font-bold text-ink">Confirm Your Consultation</h1>
            <p className="mt-1 text-sm text-ink/60">30-minute call with a Bharat Consultancy expert</p>

            <div className="stub-divider mt-6 space-y-3 pt-6">
              <div className="flex justify-between text-sm">
                <span className="text-ink/60">Name</span>
                <span className="font-medium text-ink">{booking.name}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-ink/60">Contact</span>
                <span className="text-ink">{booking.mobile} · {booking.email}</span>
              </div>
              {booking.preferredDate && (
                <div className="flex justify-between text-sm">
                  <span className="text-ink/60">Preferred Time</span>
                  <span className="text-ink">
                    {booking.preferredDate} {booking.preferredTime}
                  </span>
                </div>
              )}
              <div className="stub-divider flex justify-between pt-3 text-base font-semibold">
                <span className="text-ink">Total</span>
                <span className="font-mono text-seal-blue">₹{booking.amount.toLocaleString('en-IN')}</span>
              </div>
            </div>

            {error && <p className="mt-4 text-sm text-red-600">{error}</p>}

            <Button onClick={handleProceedToPay} className="mt-6 w-full" disabled={isPaying}>
              {isPaying ? 'Opening Payment...' : `Proceed To Pay ₹${booking.amount.toLocaleString('en-IN')}`}
            </Button>
          </div>
        </div>
      </section>
    </>
  );
}
